/**
 * scuba-brief.ts — Editorial copy layer for the SCuBA deep-scan tab brief.
 *
 * SCuBA isn't a Guardian Score pillar, so this does not reuse
 * `composeSecurityBrief`: the framing is "point-in-time audit" rather than
 * "continuous posture", and there is no score delta to narrate.
 *
 * Contract: every editorial slot is ONE deterministic, data-driven line.
 * There is no variant table and no rotation — the same input always produces
 * the same brief, and all variation in what the user reads comes from the
 * audit's counts, never from picking among synonymous phrasings of the same
 * fact.
 *
 * Precedence (headline, lede and verdict route off the same state):
 *   1. No report — no audit has run: offer the audit, never an all-clear.
 *   2. No results — an audit ran but returned no check results: re-run it.
 *   3. Failing — name where to start: the control group with the most failing
 *      critical checks in the weakest failing workload (once the detailed
 *      report is loaded), else the weakest workload, else the failing count.
 *   4. Errored — checks that could not be read: re-run the audit.
 *   5. Warnings — review them.
 *   6. All clear — "Nothing to review." only when checks were read and none
 *      fail, error or warn.
 *
 * Action-first fields (blueprint §6.2 row 13): the lede names the failing
 * checks and, once the detailed report is loaded, how many of them are
 * critical (a SCuBA criticality of Shall; copy says "critical", D33). The
 * critical clause is dropped when it is unknown, zero, or larger than the
 * failing count. "Start with …" is a recommendation, not a recency claim.
 */

import { getScubaProductInfo, type ScubaControl, type ScubaReport, type ScubaSummary } from '@/types/scuba'
import type { ActionBriefFields } from '@/lib/brief-shape'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export interface ScubaBrief {
  /** Six-words-or-fewer verdict. The SCuBA tab draws no score tile, so it is not rendered there. */
  verdict: string
  /** Provenance line: when Guardian assessed the audit and how many checks it covers. Empty before the first audit. */
  byline: string
  /** Always `null`: an audit is a point in time, with no trend to show. */
  delta: null
  /** Action-first h1 (blueprint §6.2 row 13). */
  headline: string
  /** The control group or workload the headline names, set at 400 (never a pillar ink). */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence, one `**bold**` run naming the checks and their count. */
  lede: string
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                           */
/* -------------------------------------------------------------------------- */

const fmt = (v: number) => v.toLocaleString('en-US')

/** Capitalized word form for 1-9, digits from 10 up — the verdict's counts read as prose. */
function countWord1to9(n: number): string {
  const words = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  return n >= 1 && n <= 9 ? words[n] : fmt(n)
}

/** True for a SCuBA criticality of Shall ("Shall", "Shall/Not-Implemented"): the checks the UI calls critical (D33). */
const isCriticalCheck = (control: ScubaControl) => /^shall\b/i.test((control.criticality ?? '').trim())

/**
 * Where to start: in the weakest workload (by pass rate) that has a failing
 * check, the control group with the most failing critical checks (else the
 * most failing checks). A recommendation, not a recency or ranking claim.
 */
function pickStartingGroup(report: ScubaReport): { group: string; workload: string } | null {
  const workloads = [...report.products]
    .filter(p => p.controlGroups?.some(g => g.controls?.some(c => c.result === 'Fail')))
    .sort((a, b) => (a.complianceScore ?? 100) - (b.complianceScore ?? 100))
  const weakest = workloads[0]
  if (!weakest) return null
  let best: { group: string; critical: number; failing: number } | null = null
  for (const group of weakest.controlGroups ?? []) {
    const failing = (group.controls ?? []).filter(c => c.result === 'Fail')
    if (failing.length === 0) continue
    const critical = failing.filter(isCriticalCheck).length
    if (!best || critical > best.critical || (critical === best.critical && failing.length > best.failing)) {
      best = { group: group.groupName, critical, failing: failing.length }
    }
  }
  if (!best || !best.group) return null
  return { group: best.group, workload: getScubaProductInfo(weakest.productName).displayName }
}

/** The failing critical (Shall) checks in the report; null while the report is unknown. */
function failingCriticalCount(report: ScubaReport | null): number | null {
  if (!report) return null
  let count = 0
  for (const product of report.products) {
    for (const group of product.controlGroups ?? []) {
      for (const control of group.controls ?? []) {
        if (control.result === 'Fail' && isCriticalCheck(control)) count++
      }
    }
  }
  return count
}

/* -------------------------------------------------------------------------- */
/* Copy tables — one line per slot, tokens filled from data                   */
/* -------------------------------------------------------------------------- */

const VERDICT = {
  NO_REPORT: 'No audit yet.',
  NO_RESULTS: 'No check results yet.',
  FAILING_ONE: 'One failing check to close.',
  FAILING_FEW: '{N} failing checks to close.',
  ERRORED: 'Re-run the audit.',
  WARNING_ONE: 'One warning to review.',
  WARNING_FEW: '{N} warnings to review.',
  CLEAR: 'Nothing to review.',
}

/** Action-first h1 (blueprint §6.2 row 13), in the precedence above. */
const HEADLINE_ACTION = {
  NO_REPORT: 'Run the CISA SCuBA audit to get a point-in-time report.',
  NO_RESULTS: 'Re-run the SCuBA audit to read the checks.',
  START_GROUP: 'Start with {group} in {workload} this week.',
  START_WORKLOAD: 'Start with {workload} this week.',
  CLOSE_ONE: 'Close the failing SCuBA check this week.',
  CLOSE_FEW: 'Close the {n} failing SCuBA checks this week.',
  ERRORED_ONE: 'Re-run the audit to read the check that errored.',
  ERRORED_FEW: 'Re-run the audit to read the {n} checks that errored.',
  WARNING_ONE: 'Review the SCuBA warning this week.',
  WARNING_FEW: 'Review the {n} SCuBA warnings this week.',
  CLEAR: 'Nothing to review.',
}

/** The lede: the failing checks with their critical share, else the state's one fact. */
const LEDE = {
  NO_REPORT: '**No SCuBA audit has run for this tenant yet**, so there are no findings to review.',
  NO_RESULTS: '**The last SCuBA audit returned no check results**, so Guardian has nothing to show yet.',
  FAILING_ONE: '**1 SCuBA check is failing**',
  FAILING_FEW: '**{n} SCuBA checks are failing**',
  CRITICAL_SAME: ', and it is critical',
  CRITICAL_ONE: ', and 1 of them is critical',
  CRITICAL_FEW: ', and {c} of them are critical',
  ERRORED_ONE: '**1 SCuBA check could not be read**, and none are failing.',
  ERRORED_FEW: '**{n} SCuBA checks could not be read**, and none are failing.',
  WARNING_ONE: '**1 SCuBA check raised a warning**, and none are failing.',
  WARNING_FEW: '**{n} SCuBA checks raised a warning**, and none are failing.',
  CLEAR_ONE: '**1 SCuBA check passes**, and none are failing.',
  CLEAR_FEW: '**{n} SCuBA checks pass**, and none are failing.',
}

/** The provenance line: the scan time when known, and the checks the audit covers. */
const BYLINE = {
  DATED: 'Assessed by Guardian on {date} · {n} CISA SCuBA checks',
  UNDATED: 'Assessed by Guardian · {n} CISA SCuBA checks',
}

/* -------------------------------------------------------------------------- */
/* Lede                                                                       */
/* -------------------------------------------------------------------------- */

function failingLede(failures: number, critical: number | null): string {
  const head = failures === 1 ? LEDE.FAILING_ONE : LEDE.FAILING_FEW.replace('{n}', fmt(failures))
  if (critical === null || critical === 0 || critical > failures) return `${head}.`
  if (failures === 1) return `${head}${LEDE.CRITICAL_SAME}.`
  if (critical === 1) return `${head}${LEDE.CRITICAL_ONE}.`
  return `${head}${LEDE.CRITICAL_FEW.replace('{c}', fmt(critical))}.`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose the SCuBA brief from the scan summary and, once loaded, the detailed
 * report (it names the starting control group and the critical share). With
 * no summary, or one without a report, it returns the brief that offers the
 * first audit; an audit with no check results never reads as all-clear.
 */
export function composeScubaBrief(
  summary: ScubaSummary | null | undefined,
  report: ScubaReport | null = null,
): ScubaBrief {
  if (!summary?.hasReport) {
    return { verdict: VERDICT.NO_REPORT, byline: '', delta: null, headline: HEADLINE_ACTION.NO_REPORT, lede: LEDE.NO_REPORT }
  }

  const failures = summary.totalFailures ?? 0
  const errors = summary.totalErrors ?? 0
  const warnings = summary.totalWarnings ?? 0
  const passes = summary.totalPasses ?? 0

  const scanDate = summary.lastScanDate ? new Date(summary.lastScanDate) : null
  const checks = fmt(summary.totalControls ?? 0)
  const byline =
    scanDate && !Number.isNaN(scanDate.getTime())
      ? BYLINE.DATED.replace(
          '{date}',
          scanDate.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
        ).replace('{n}', checks)
      : BYLINE.UNDATED.replace('{n}', checks)

  if (failures + errors + warnings + passes === 0) {
    return { verdict: VERDICT.NO_RESULTS, byline, delta: null, headline: HEADLINE_ACTION.NO_RESULTS, lede: LEDE.NO_RESULTS }
  }

  if (failures > 0) {
    const start = report ? pickStartingGroup(report) : null
    // The weakest workload by pass rate, for when the detailed report is not loaded yet.
    const weakest = [...(summary.products ?? [])].sort(
      (a, b) => (a.complianceScore ?? 100) - (b.complianceScore ?? 100),
    )[0]
    const weakestLabel = weakest ? getScubaProductInfo(weakest.productName).displayName : null

    let headline: string
    let emphasis: ActionBriefFields['emphasis']
    if (start) {
      headline = HEADLINE_ACTION.START_GROUP.replace('{group}', start.group).replace('{workload}', start.workload)
      emphasis = { text: start.group }
    } else if (weakestLabel) {
      headline = HEADLINE_ACTION.START_WORKLOAD.replace('{workload}', weakestLabel)
      emphasis = { text: weakestLabel }
    } else {
      headline = failures === 1 ? HEADLINE_ACTION.CLOSE_ONE : HEADLINE_ACTION.CLOSE_FEW.replace('{n}', fmt(failures))
    }
    return {
      verdict: failures === 1 ? VERDICT.FAILING_ONE : VERDICT.FAILING_FEW.replace('{N}', countWord1to9(failures)),
      byline,
      delta: null,
      headline,
      ...(emphasis ? { emphasis } : {}),
      lede: failingLede(failures, failingCriticalCount(report)),
    }
  }

  if (errors > 0) {
    return {
      verdict: VERDICT.ERRORED,
      byline,
      delta: null,
      headline: errors === 1 ? HEADLINE_ACTION.ERRORED_ONE : HEADLINE_ACTION.ERRORED_FEW.replace('{n}', fmt(errors)),
      lede: errors === 1 ? LEDE.ERRORED_ONE : LEDE.ERRORED_FEW.replace('{n}', fmt(errors)),
    }
  }

  if (warnings > 0) {
    return {
      verdict: warnings === 1 ? VERDICT.WARNING_ONE : VERDICT.WARNING_FEW.replace('{N}', countWord1to9(warnings)),
      byline,
      delta: null,
      headline: warnings === 1 ? HEADLINE_ACTION.WARNING_ONE : HEADLINE_ACTION.WARNING_FEW.replace('{n}', fmt(warnings)),
      lede: warnings === 1 ? LEDE.WARNING_ONE : LEDE.WARNING_FEW.replace('{n}', fmt(warnings)),
    }
  }

  return {
    verdict: VERDICT.CLEAR,
    byline,
    delta: null,
    headline: HEADLINE_ACTION.CLEAR,
    lede: passes === 1 ? LEDE.CLEAR_ONE : LEDE.CLEAR_FEW.replace('{n}', fmt(passes)),
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Copy tables exposed for exact-string assertions in tests (and for a
 *  generic copy-lint sweep). Not part of the public runtime API; do not
 *  import from app code. */
export const __TEST_ONLY__ = {
  VERDICT,
  HEADLINE_ACTION,
  LEDE,
  BYLINE,
}
